import React from 'react';

import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { Image, Description, Title, Subtitle, listItemBaseStyles } from 'components/ui/list-item-label'
import { Container, Content, Wrap, ItemIndex, DragHandle } from 'components/ui/draggable'

import { EventPointOfInterest } from 'generated/mos/org/getty/audio'

import { IconHash, IconLanguages, IconAudioTrack, IconRoom, IconWarning } from 'components/ui/icons'
import { space } from 'helpers/style'
import styled from 'styled'

import { AttributeRef } from 'generated/mos/entity'
import { bffEndpoint } from 'helpers/bff'
import { Media } from 'generated/mos/media'
import { eventPointOfInterestSchema } from '../../schemas/event-point-of-interest'
import { Category } from 'generated/mos/org/getty/pointofinterest'
import { calendarTitles } from './helper'

const Warning = styled.div`
  width: 20px;
`

const IconBlock = styled.div`
  display: flex;
  align-items: center;
  margin: 0 10px;
  font-weight: ${({ theme }) => theme.font.weight.base};
  color: ${({ theme }) => theme.color.grayL40};
`;

const IconText = styled.span<{ wide?: boolean }>`
  width: ${({ wide }) => wide ? space(45) : space(10)};
  margin-left: ${space(2)};
  font-size: ${({ theme }) => theme.font.size.small};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 0;
`

const DisplayBadge = styled.div`
  white-space: nowrap;
`;

type Props = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly pointOfInterest: EventPointOfInterest.Entity;
  readonly deleteFromMood?: (pointOfInterestRef: EventPointOfInterest.Ref) => void;
  readonly moodId?: string;
  readonly placement?: string;
  readonly site?: string;
  readonly hasWarning?: boolean;
  readonly draggableIndex?: number;
  readonly isDragging?: boolean;
};

export const ListItem: React.FC<Props> = ({
  deleteFromMood,
  historyPush,
  projectId,
  pointOfInterest,
  moodId,
  placement,
  site,
  hasWarning,
  draggableIndex,
  isDragging,
}) => {
  const hostName = bffEndpoint(window.location.hostname)
  const pointOfInterestId = EventPointOfInterest.mustRef(pointOfInterest.ref).id
  const editStopPointOfInterestLocation = `/project/${projectId}/mood/${moodId}/event-point-of-interest/${pointOfInterestId}`

  const imagePath = pointOfInterest && pointOfInterest.event ? pointOfInterest.event.imageSmallSquareUrl : ''
  const title = pointOfInterest && pointOfInterest.event ? pointOfInterest.event.title : ''

  const calendars = pointOfInterest && pointOfInterest.event ? calendarTitles(pointOfInterest.event) : []

  return(
    <Container isDragging={isDragging} onClick={() => historyPush(editStopPointOfInterestLocation)}>
      <Wrap isDragging={isDragging}>
        <Content>
          {draggableIndex !== undefined && (
            <>
              <DragHandle />
              <ItemIndex>
                {draggableIndex + 1}
              </ItemIndex>
            </>
          )}
          <Image src={imagePath} />
          <Description>
            <Title>{title}</Title>
            <DisplayBadge>
              {calendars}
              <Subtitle>{`${site ? site : ''}`}</Subtitle>
            </DisplayBadge>
          </Description>
        </Content>
        <IconsContainer>
          <IconBlock>
            <IconAudioTrack color="grayL40" size="small" />
            <IconText>{pointOfInterest.audio ? 'M4A' : '-'}</IconText>
          </IconBlock>
          <IconBlock>
            <IconRoom color="grayL40" size="small" />
            <IconText wide>{placement ? placement : '-'}</IconText>
          </IconBlock>
          <IconBlock>
            <Warning>{hasWarning && <IconWarning size="small" color="warning" />}</Warning>
          </IconBlock>
        </IconsContainer>
        <PopOverMenu iconSize="base">
          <PopOverMenuButton
            onClick={(event) => {
              historyPush(editStopPointOfInterestLocation)
              event.stopPropagation()
            }}
          >
            Edit Point of Interest
          </PopOverMenuButton>
          {deleteFromMood && (
            <PopOverMenuButton onClick={(event) => {
                deleteFromMood(EventPointOfInterest.mustRef(pointOfInterest.ref))
                event.stopPropagation()
              }
            }>
              Remove from Mood Journey
            </PopOverMenuButton>
          )}
        </PopOverMenu>
      </Wrap>
    </Container>
  )
}
